/**
 * 跨域 LocalStorage 同步（使用 WebSocket 中繼）
 * 
 * 由於瀏覽器安全限制，不同域名的 localStorage 無法直接同步
 * 這個方案使用 WebSocket 作為中繼服務器來實現跨域同步
 * 
 * 架構：
 * localhost:5173 <--WebSocket--> API Server (localhost:3001) <--WebSocket--> 192.168.x.x:5173
 */

export interface CrossOriginSyncConfig {
  wsUrl: string;              // WebSocket 服務器地址
  keys: string[];             // 要同步的 localStorage keys
  clientId?: string;          // 客戶端 ID（用於識別）
  autoSync?: boolean;         // 是否自動同步
  debug?: boolean;            // 調試模式
}

export class CrossOriginSync {
  private config: CrossOriginSyncConfig;
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private clientId: string;

  constructor(config: CrossOriginSyncConfig) {
    this.config = {
      autoSync: true,
      debug: false,
      ...config
    };

    this.clientId = config.clientId || this.generateClientId();

    if (this.config.autoSync) {
      this.connect();
      this.setupStorageListener();
    }

    this.log('✅ CrossOriginSync 已初始化', {
      wsUrl: this.config.wsUrl,
      clientId: this.clientId,
      keys: this.config.keys
    });
  }

  /**
   * 生成客戶端 ID
   */
  private generateClientId(): string {
    return `${window.location.hostname}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 記錄日誌
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[CrossOriginSync] ${message}`, data || '');
    }
  }

  /**
   * 連接到 WebSocket 服務器
   */
  private connect(): void {
    try {
      this.log('🔌 連接到 WebSocket 服務器...');
      this.ws = new WebSocket(this.config.wsUrl);

      this.ws.onopen = () => {
        this.log('✅ WebSocket 已連接');
        this.reconnectAttempts = 0;

        // 註冊客戶端
        this.send({
          type: 'register',
          clientId: this.clientId,
          origin: window.location.origin,
          keys: this.config.keys
        });

        // 請求初始同步
        this.requestSync();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event);
      };

      this.ws.onclose = () => {
        this.log('🔌 WebSocket 已斷開');
        this.ws = null;
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        this.log('❌ WebSocket 錯誤:', error);
      };
    } catch (error) {
      this.log('❌ 連接失敗:', error);
      this.attemptReconnect();
    }
  }

  /**
   * 嘗試重新連接
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log('⚠️ 已達到最大重連次數，停止重連');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    this.reconnectAttempts++;

    this.log(`🔄 ${delay}ms 後嘗試重連 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * 處理收到的消息
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'sync':
          this.handleSyncMessage(data);
          break;

        case 'syncAll':
          this.handleSyncAllMessage(data);
          break;

        case 'registered':
          this.log('✅ 客戶端已註冊');
          break;

        default:
          this.log('⚠️ 未知消息類型:', data.type);
      }
    } catch (error) {
      this.log('❌ 處理消息失敗:', error);
    }
  }

  /**
   * 處理單個 key 的同步消息
   */
  private handleSyncMessage(data: any): void {
    const { key, value, fromClientId } = data;

    // 跳過自己發送的消息
    if (fromClientId === this.clientId) {
      return;
    }

    if (!this.config.keys.includes(key)) {
      return;
    }

    this.log(`📥 收到同步: ${key}`);

    // 更新本地 localStorage
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }

    this.log(`✅ 已更新本地 ${key}`);

    // 觸發自定義事件（供應用監聽）
    window.dispatchEvent(new CustomEvent('echov-sync', {
      detail: { key, value, fromClientId }
    }));
  }

  /**
   * 處理批量同步消息
   */
  private handleSyncAllMessage(data: any): void {
    const { data: syncData, fromClientId } = data;

    // 跳過自己發送的消息
    if (fromClientId === this.clientId) {
      return;
    }

    this.log('📥 收到批量同步');

    Object.entries(syncData).forEach(([key, value]) => {
      if (this.config.keys.includes(key)) {
        if (value === null) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, value as string);
        }
      }
    });

    this.log('✅ 批量同步完成');
  }

  /**
   * 設置 localStorage 監聽器
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (e) => {
      if (e.key && this.config.keys.includes(e.key)) {
        this.log(`🔄 檢測到 ${e.key} 變化，準備同步...`);
        this.syncKey(e.key, e.newValue);
      }
    });

    // 監聽 localStorage 的 setItem/removeItem（需要 hook）
    this.hookLocalStorage();

    this.log('✅ Storage 監聽器已設置');
  }

  /**
   * Hook localStorage 方法以監聽變化
   */
  private hookLocalStorage(): void {
    const originalSetItem = localStorage.setItem.bind(localStorage);
    const originalRemoveItem = localStorage.removeItem.bind(localStorage);

    localStorage.setItem = (key: string, value: string) => {
      originalSetItem(key, value);
      
      if (this.config.keys.includes(key)) {
        this.log(`🔄 檢測到 setItem: ${key}`);
        this.syncKey(key, value);
      }
    };

    localStorage.removeItem = (key: string) => {
      originalRemoveItem(key);
      
      if (this.config.keys.includes(key)) {
        this.log(`🔄 檢測到 removeItem: ${key}`);
        this.syncKey(key, null);
      }
    };
  }

  /**
   * 發送消息到 WebSocket 服務器
   */
  private send(message: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log('⚠️ WebSocket 未連接，無法發送消息');
      return;
    }

    try {
      this.ws.send(JSON.stringify(message));
    } catch (error) {
      this.log('❌ 發送消息失敗:', error);
    }
  }

  /**
   * 同步單個 key
   */
  public syncKey(key: string, value: string | null): void {
    if (!this.config.keys.includes(key)) {
      this.log(`⚠️ ${key} 不在同步列表中`);
      return;
    }

    this.log(`📤 同步 ${key}`);

    this.send({
      type: 'sync',
      clientId: this.clientId,
      key,
      value,
      timestamp: Date.now()
    });
  }

  /**
   * 同步所有 keys
   */
  public syncAll(): void {
    this.log('🔄 同步所有配置...');

    const data: Record<string, string | null> = {};
    
    this.config.keys.forEach(key => {
      data[key] = localStorage.getItem(key);
    });

    this.send({
      type: 'syncAll',
      clientId: this.clientId,
      data,
      timestamp: Date.now()
    });

    this.log('✅ 同步完成');
  }

  /**
   * 請求從其他客戶端同步
   */
  public requestSync(): void {
    this.log('📥 請求同步...');

    this.send({
      type: 'requestSync',
      clientId: this.clientId,
      keys: this.config.keys,
      timestamp: Date.now()
    });
  }

  /**
   * 斷開連接
   */
  public disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.log('🛑 已斷開連接');
  }

  /**
   * 啟用調試模式
   */
  public enableDebug(): void {
    this.config.debug = true;
    this.log('🐛 調試模式已啟用');
  }

  /**
   * 禁用調試模式
   */
  public disableDebug(): void {
    this.config.debug = false;
  }
}

/**
 * 全局同步實例
 */
let globalSyncInstance: CrossOriginSync | null = null;

export function initCrossOriginSync(config: CrossOriginSyncConfig): CrossOriginSync {
  if (globalSyncInstance) {
    globalSyncInstance.disconnect();
  }

  globalSyncInstance = new CrossOriginSync(config);

  // 暴露到全局
  (window as any).EchoVCrossSync = globalSyncInstance;

  return globalSyncInstance;
}

export function getCrossOriginSync(): CrossOriginSync | null {
  return globalSyncInstance;
}
